import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Spinner,
  Box,
  Divider,
  FormLayout,
  TextField,
  Checkbox,
  Select,
  Banner,
  Badge,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';
import ZyzfyEmailSettings from '../components/ZyzfyEmailSettings';

function Settings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [settings, setSettings] = useState({
    emailEnabled: true,
    defaultLanguage: 'pt-BR',
    timezonOffset: -3,
    emailFromName: '',
    supportEmail: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Wait longer for App Bridge to initialize and URL to be fixed
    const timer = setTimeout(() => {
      loadSettings();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Load shop info
      const shopResponse = await api.get('/shopify/shop');
      setShopInfo(shopResponse.data);
      
      // Load real settings from API
      try {
        const settingsResponse = await api.get('/settings');
        setSettings({
          emailEnabled: true,
          defaultLanguage: 'pt-BR',
          timezonOffset: -3,
          emailFromName: shopResponse.data.name || '',
          supportEmail: shopResponse.data.email || '',
          ...settingsResponse.data
        });
      } catch (settingsError) {
        // If no settings found, use defaults with shop data
        setSettings({
          emailEnabled: true,
          defaultLanguage: 'pt-BR',
          timezonOffset: -3,
          emailFromName: shopResponse.data.name || '',
          supportEmail: shopResponse.data.email || ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setErrors({});
      setSuccess(false);
      
      // Validate settings
      const newErrors = {};
      
      if (settings.emailFromName.trim() === '') {
        newErrors.emailFromName = 'Nome do remetente é obrigatório';
      }
      
      if (settings.supportEmail && !isValidEmail(settings.supportEmail)) {
        newErrors.supportEmail = 'Email de suporte inválido';
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      
      // Save settings to API
      await api.put('/settings', settings);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const languageOptions = [
    { label: 'Português (Brasil)', value: 'pt-BR' },
    { label: 'English (US)', value: 'en-US' },
    { label: 'Español', value: 'es-ES' }
  ];

  const timezoneOptions = [
    { label: 'Brasília (UTC-3)', value: -3 },
    { label: 'São Paulo (UTC-3)', value: -3 },
    { label: 'Rio de Janeiro (UTC-3)', value: -3 },
    { label: 'UTC', value: 0 },
    { label: 'New York (UTC-5)', value: -5 }
  ];

  if (loading) {
    return (
      <Page title={t('settings.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('settings.loadingSettings')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('settings.title')}
      subtitle={t('settings.subtitle')}
      primaryAction={{
        content: t('settings.saveSettings'),
        onAction: saveSettings,
        loading: saving
      }}
    >
      <Layout>
        {success && (
          <Layout.Section>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #22c55e',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#166534', margin: '0' }}>{t('settings.settingsSavedSuccess')}</h4>
            </div>
          </Layout.Section>
        )}

        {/* Store Information */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h3">
                {t('settings.storeInfo')}
              </Text>
            </Box>
            <Divider />
            <Box padding="400">
              {shopInfo && (
                <FormLayout>
                  <TextField
                    label={t('settings.storeName')}
                    value={shopInfo.name || ''}
                    disabled
                    helpText={t('settings.storeNameDescription')}
                  />
                  
                  <TextField
                    label={t('settings.storeDomain')}
                    value={shopInfo.domain || ''}
                    disabled
                    helpText={t('settings.storeDomainDescription')}
                  />
                  
                  <TextField
                    label={t('settings.storeEmail')}
                    value={shopInfo.email || ''}
                    disabled
                    helpText={t('settings.storeEmailDescription')}
                  />
                </FormLayout>
              )}
            </Box>
          </Card>
        </Layout.Section>

        {/* Email Settings */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h3">
                {t('settings.emailSettings')}
              </Text>
            </Box>
            <Divider />
            <Box padding="400">
              <FormLayout>
                <TextField
                  label={t('settings.senderName')}
                  value={settings.emailFromName}
                  onChange={(value) => handleSettingChange('emailFromName', value)}
                  error={errors.emailFromName}
                  helpText={t('settings.senderNameDescription')}
                />
                
                <TextField
                  label={t('settings.supportEmail')}
                  value={settings.supportEmail}
                  onChange={(value) => handleSettingChange('supportEmail', value)}
                  error={errors.supportEmail}
                  helpText={t('settings.supportEmailDescription')}
                />
              </FormLayout>
            </Box>
          </Card>
        </Layout.Section>


        {/* API & Integration */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h3">
                {t('settings.apiIntegrations')}
              </Text>
            </Box>
            <Divider />
            <Box padding="400">
              <Box paddingBlockEnd="400">
                <Text variant="bodyMd" as="p">
                  <strong>{t('settings.webhookUrl')}</strong>
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  {process.env.NODE_ENV === 'production' 
                    ? `${window.location.origin}/api/webhooks/orders/create`
                    : 'Configure em produção'
                  }
                </Text>
              </Box>
              
              <Text variant="bodyMd" as="p" color="subdued">
                {t('settings.webhookDescription')}
              </Text>
              
              <Divider />
              
              {/* Discreet Zyzfy Integration */}
              <Box paddingBlockStart="400">
                <ZyzfyEmailSettings />
              </Box>
            </Box>
          </Card>
        </Layout.Section>

        {/* Privacy Policy Link */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="bodyMd" as="p">
                <a href="/pages/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#2c6ecb' }}>
                  {t('settings.privacyPolicy', 'Política de Privacidade')}
                </a>
              </Text>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Settings;